import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  ShieldCheck, Lock, Unlock, Plus, Eye, EyeOff, Copy, Trash2, Pencil,
  KeyRound, CreditCard, Car, BookUser, Landmark, HeartPulse, FileKey2, CalendarDays, ScrollText, X,
  Paperclip, ImageIcon, Download, FileText,
} from "lucide-react";

// ---------------- Types ----------------

interface VaultStatus {
  isSetUp: boolean;
  isUnlocked: boolean;
  unlockExpiresAt: string | null;
  lockedUntil: string | null;
}

interface VaultFileMeta {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

interface VaultRecordMeta {
  id: string;
  name: string;
  personName: string | null;
  category: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  maskedFields: Record<string, string> | null;
  files?: VaultFileMeta[];
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function readFileAsBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

interface AuditEntry {
  id: string;
  action: string;
  recordName: string | null;
  context: string | null;
  createdAt: string;
}

// ---------------- Category templates ----------------

const CATEGORIES: { value: string; label: string; icon: any; fields: string[] }[] = [
  { value: "ssn", label: "Social Security Number", icon: KeyRound, fields: ["Social Security Number"] },
  { value: "dob", label: "Date of Birth", icon: CalendarDays, fields: ["Date of Birth"] },
  { value: "credit-card", label: "Credit Card", icon: CreditCard, fields: ["Cardholder Name", "Card Number", "Expiration", "CVV"] },
  { value: "drivers-license", label: "Driver's License", icon: Car, fields: ["License Number", "State", "Expiration"] },
  { value: "passport", label: "Passport", icon: BookUser, fields: ["Passport Number", "Country", "Expiration"] },
  { value: "bank-account", label: "Bank Account", icon: Landmark, fields: ["Bank Name", "Routing Number", "Account Number"] },
  { value: "insurance", label: "Insurance", icon: HeartPulse, fields: ["Provider", "Member ID", "Group Number"] },
  { value: "custom", label: "Custom", icon: FileKey2, fields: [] },
];

function categoryMeta(value: string) {
  return CATEGORIES.find((c) => c.value === value) ?? CATEGORIES[CATEGORIES.length - 1];
}

const ACTION_LABELS: Record<string, string> = {
  setup: "Vault created",
  unlock: "Vault unlocked",
  unlock_failed: "Failed unlock attempt",
  lock: "Vault locked",
  create: "Record added",
  update: "Record updated",
  delete: "Record deleted",
  reveal: "Record revealed",
  agent_redact: "PII protected from AI",
  password_change: "Vault password changed",
  reset: "Vault reset (all records deleted)",
  file_add: "File attached",
  file_view: "File viewed",
  file_delete: "File deleted",
};

// ---------------- Page ----------------

export default function VaultPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status, isLoading: statusLoading } = useQuery<VaultStatus>({
    queryKey: ["/api/vault/status"],
    refetchInterval: 30000,
  });

  const invalidateVault = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/vault/status"] });
    queryClient.invalidateQueries({ queryKey: ["/api/vault/records"] });
    queryClient.invalidateQueries({ queryKey: ["/api/vault/audit"] });
  };

  return (
    <div className="w-full p-4 md:p-8 space-y-6" data-testid="page-vault">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="text-vault-title">
            <ShieldCheck className="w-7 h-7 text-primary" />
            Vault
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Encrypted storage for your family's sensitive information — SSNs, birth dates, credit cards,
            licenses and more. Protected by a separate vault password, and automatically shielded from AI:
            any vault value in a message to Zeus or an agent task is replaced with a secure token before it
            leaves the platform.
          </p>
        </div>
        {status?.isSetUp && status.isUnlocked && <LockButton onLocked={invalidateVault} />}
      </div>

      {statusLoading ? (
        <div data-testid="loading-vault">Loading vault…</div>
      ) : !status?.isSetUp ? (
        <SetupCard onDone={invalidateVault} />
      ) : !status.isUnlocked ? (
        <UnlockCard lockedUntil={status.lockedUntil} onUnlocked={invalidateVault} />
      ) : (
        <UnlockedVault unlockExpiresAt={status.unlockExpiresAt} onChanged={invalidateVault} />
      )}
    </div>
  );
}

// ---------------- Setup ----------------

function SetupCard({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const setupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/vault/setup", { password });
      if (!res.ok) throw new Error((await res.json()).error || "Setup failed");
    },
    onSuccess: () => {
      toast({ title: "Vault created", description: "Your vault is set up and unlocked." });
      onDone();
    },
    onError: (e: any) => toast({ title: "Could not create vault", description: e.message, variant: "destructive" }),
  });

  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Create your vault password</CardTitle>
        <CardDescription>
          This password is separate from your login. Even someone signed in to your account cannot open the
          vault without it. <strong>It cannot be recovered</strong> — if you forget it, your vault data is
          permanently unreadable.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="vault-setup-password">Vault password (min 8 characters)</Label>
          <Input id="vault-setup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-vault-setup-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="vault-setup-confirm">Confirm password</Label>
          <Input id="vault-setup-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="input-vault-setup-confirm" />
          {mismatch && <p className="text-sm text-destructive">Passwords do not match</p>}
        </div>
        <Button
          disabled={password.length < 8 || password !== confirm || setupMutation.isPending}
          onClick={() => setupMutation.mutate()}
          data-testid="button-vault-setup"
        >
          {setupMutation.isPending ? "Creating…" : "Create Vault"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------- Unlock ----------------

function UnlockCard({ lockedUntil, onUnlocked }: { lockedUntil: string | null; onUnlocked: () => void }) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [resetOpen, setResetOpen] = useState(false);

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/vault/unlock", { password });
      if (!res.ok) throw new Error((await res.json()).error || "Unlock failed");
    },
    onSuccess: () => {
      setPassword("");
      onUnlocked();
    },
    onError: (e: any) => toast({ title: "Unlock failed", description: e.message, variant: "destructive" }),
  });

  const isLockedOut = lockedUntil && new Date(lockedUntil).getTime() > Date.now();

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Vault locked</CardTitle>
        <CardDescription>Enter your vault password to view or manage records.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLockedOut && (
          <p className="text-sm text-destructive" data-testid="text-vault-lockout">
            Too many failed attempts. Try again after {new Date(lockedUntil!).toLocaleTimeString()}.
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="vault-unlock-password">Vault password</Label>
          <Input
            id="vault-unlock-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && password && !isLockedOut) unlockMutation.mutate(); }}
            data-testid="input-vault-unlock-password"
          />
        </div>
        <Button disabled={!password || !!isLockedOut || unlockMutation.isPending} onClick={() => unlockMutation.mutate()} data-testid="button-vault-unlock">
          <Unlock className="w-4 h-4 mr-2" />
          {unlockMutation.isPending ? "Unlocking…" : "Unlock Vault"}
        </Button>
        <div className="pt-2 border-t">
          <p className="text-sm text-muted-foreground mb-2">
            Forgot your vault password? There is no recovery key — for your security, the only option is to
            reset the vault, which permanently deletes all stored records.
          </p>
          <Button variant="destructive" size="sm" onClick={() => setResetOpen(true)} data-testid="button-vault-reset-open">
            <Trash2 className="w-4 h-4 mr-2" /> Reset Vault
          </Button>
        </div>
        <ResetVaultDialog open={resetOpen} onOpenChange={setResetOpen} onReset={onUnlocked} />
      </CardContent>
    </Card>
  );
}

function ResetVaultDialog({ open, onOpenChange, onReset }: { open: boolean; onOpenChange: (o: boolean) => void; onReset: () => void }) {
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) setConfirmText("");
  }, [open]);

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/vault/reset", { confirm: "RESET VAULT" });
      if (!res.ok) throw new Error((await res.json()).error || "Reset failed");
      return res.json();
    },
    onSuccess: (data: any) => {
      onOpenChange(false);
      toast({
        title: "Vault reset",
        description: `All records were permanently deleted (${data?.deletedRecords ?? 0}). You can now set up a new vault.`,
      });
      onReset();
    },
    onError: (e: any) => toast({ title: "Reset failed", description: e.message, variant: "destructive" }),
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset vault?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes every record in your vault. Encrypted data cannot be recovered without
            your vault password — there is no recovery key. Type <span className="font-semibold">RESET VAULT</span> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="RESET VAULT"
          data-testid="input-vault-reset-confirm"
        />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-vault-reset-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={confirmText !== "RESET VAULT" || resetMutation.isPending}
            onClick={(e) => { e.preventDefault(); resetMutation.mutate(); }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="button-vault-reset-confirm"
          >
            {resetMutation.isPending ? "Resetting…" : "Permanently delete everything"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function LockButton({ onLocked }: { onLocked: () => void }) {
  const lockMutation = useMutation({
    mutationFn: async () => { await apiRequest("POST", "/api/vault/lock"); },
    onSuccess: onLocked,
  });
  return (
    <Button variant="outline" onClick={() => lockMutation.mutate()} data-testid="button-vault-lock">
      <Lock className="w-4 h-4 mr-2" /> Lock Vault
    </Button>
  );
}

// ---------------- Unlocked vault ----------------

function UnlockedVault({ unlockExpiresAt, onChanged }: { unlockExpiresAt: string | null; onChanged: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<VaultRecordMeta | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<VaultRecordMeta | null>(null);
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    if (!unlockExpiresAt) return;
    const tick = () => {
      const ms = new Date(unlockExpiresAt).getTime() - Date.now();
      if (ms <= 0) {
        setRemaining("expired");
        onChanged();
        return;
      }
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining(`${m}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [unlockExpiresAt]);

  const { data: recordsData, isLoading } = useQuery<{ records: VaultRecordMeta[]; isUnlocked: boolean }>({
    queryKey: ["/api/vault/records"],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/vault/records/${id}`);
      if (!res.ok) throw new Error((await res.json()).error || "Delete failed");
    },
    onSuccess: () => {
      toast({ title: "Record deleted" });
      setDeleteRecord(null);
      queryClient.invalidateQueries({ queryKey: ["/api/vault/records"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vault/audit"] });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const records = recordsData?.records ?? [];
  const grouped = useMemo(() => {
    const map = new Map<string, VaultRecordMeta[]>();
    for (const r of records) {
      const key = r.personName?.trim() || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [records]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Unlock className="w-4 h-4 text-green-600" />
          Vault unlocked{remaining && remaining !== "expired" ? ` — auto-locks in ${remaining}` : ""}
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="button-add-vault-record">
          <Plus className="w-4 h-4 mr-2" /> Add Record
        </Button>
      </div>

      <Tabs defaultValue="records" className="w-full">
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-vault-records">Records ({records.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-vault-audit">Activity Log</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-vault-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="space-y-6 mt-4">
          {isLoading ? (
            <div>Loading records…</div>
          ) : records.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground" data-testid="text-vault-empty">
                No records yet. Add SSNs, birth dates, credit cards, licenses and more — for yourself or any
                family member on your plan.
              </CardContent>
            </Card>
          ) : (
            grouped.map(([person, recs]) => (
              <div key={person} className="space-y-3">
                <h2 className="text-lg font-semibold" data-testid={`vault-group-${person}`}>{person}</h2>
                <div className="border rounded-lg divide-y">
                  {recs.map((rec) => (
                    <RecordRow
                      key={rec.id}
                      record={rec}
                      onEdit={() => setEditRecord(rec)}
                      onDelete={() => setDeleteRecord(rec)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditLog />
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <SecurityTab />
        </TabsContent>
      </Tabs>

      <RecordDialog open={addOpen} onClose={() => setAddOpen(false)} record={null} />
      <RecordDialog open={!!editRecord} onClose={() => setEditRecord(null)} record={editRecord} />

      <AlertDialog open={!!deleteRecord} onOpenChange={(o) => { if (!o) setDeleteRecord(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteRecord?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the encrypted record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRecord && deleteMutation.mutate(deleteRecord.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-record"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------- Record row (list view) ----------------

function RecordRow({ record, onEdit, onDelete }: { record: VaultRecordMeta; onEdit: () => void; onDelete: () => void }) {
  const { toast } = useToast();
  const [revealed, setRevealed] = useState<Record<string, string> | null>(null);

  const revealMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/vault/records/${record.id}/reveal`);
      if (!res.ok) throw new Error((await res.json()).error || "Reveal failed");
      return (await res.json()).fields as Record<string, string>;
    },
    onSuccess: (fields) => setRevealed(fields),
    onError: (e: any) => toast({ title: "Could not reveal", description: e.message, variant: "destructive" }),
  });

  const meta = categoryMeta(record.category);
  const Icon = meta.icon;
  const fields = revealed ?? record.maskedFields ?? {};

  const copyValue = async (key: string) => {
    try {
      let value = revealed?.[key];
      if (!value) {
        const res = await apiRequest("POST", `/api/vault/records/${record.id}/reveal`);
        if (!res.ok) throw new Error("Vault locked");
        value = ((await res.json()).fields as Record<string, string>)[key];
      }
      await navigator.clipboard.writeText(value ?? "");
      toast({ title: "Copied", description: `${key} copied to clipboard` });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <div className="px-4 py-3" data-testid={`vault-record-${record.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 shrink-0 text-primary" />
          <span className="font-medium truncate">{record.name}</span>
          <Badge variant="secondary" className="shrink-0">{meta.label}</Badge>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {revealed ? (
            <Button variant="outline" size="sm" onClick={() => setRevealed(null)} data-testid={`button-hide-${record.id}`}>
              <EyeOff className="w-4 h-4 mr-1" /> Hide
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => revealMutation.mutate()} disabled={revealMutation.isPending} data-testid={`button-reveal-${record.id}`}>
              <Eye className="w-4 h-4 mr-1" /> Reveal
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`button-edit-${record.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} data-testid={`button-delete-${record.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {Object.entries(fields).map(([key, value]) => (
          <div key={key} className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground w-48 shrink-0 truncate">{key}</span>
            <span className="font-mono flex items-center gap-1 min-w-0">
              <span className="truncate" data-testid={`vault-field-${record.id}-${key}`}>{value}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => copyValue(key)} title={`Copy ${key}`}>
                <Copy className="w-3 h-3" />
              </Button>
            </span>
          </div>
        ))}
        {!!record.files?.length && (
          <div className="pt-1 space-y-1">
            {record.files.map((f) => (
              <AttachmentRow key={f.id} file={f} recordId={record.id} />
            ))}
          </div>
        )}
        {record.notes && <p className="text-xs text-muted-foreground pt-1">{record.notes}</p>}
      </div>
    </div>
  );
}

function AttachmentRow({ file, recordId }: { file: VaultFileMeta; recordId: string }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const isImage = file.mimeType.startsWith("image/");

  const fetchBlob = async () => {
    const res = await fetch(`/api/vault/files/${file.id}`, { credentials: "include" });
    if (!res.ok) {
      let msg = "Could not open file";
      try { msg = (await res.json()).error || msg; } catch {}
      throw new Error(msg);
    }
    return res.blob();
  };

  const openFile = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      toast({ title: "Could not open file", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const downloadFile = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`vault-file-${file.id}`}>
      {isImage ? <ImageIcon className="w-4 h-4 shrink-0 text-muted-foreground" /> : <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />}
      <span className="truncate">{file.filename}</span>
      <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.sizeBytes)}</span>
      <Button variant="ghost" size="sm" className="h-6 px-2" disabled={busy} onClick={openFile} data-testid={`button-view-file-${file.id}`}>
        <Eye className="w-3.5 h-3.5 mr-1" /> View
      </Button>
      <Button variant="ghost" size="sm" className="h-6 px-2" disabled={busy} onClick={downloadFile} data-testid={`button-download-file-${file.id}`}>
        <Download className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ---------------- Add / Edit dialog ----------------

function RecordDialog({ open, onClose, record }: { open: boolean; onClose: () => void; record: VaultRecordMeta | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [personName, setPersonName] = useState("");
  const [category, setCategory] = useState("ssn");
  const [notes, setNotes] = useState("");
  const [fieldPairs, setFieldPairs] = useState<{ key: string; value: string }[]>([]);
  const [loadedForEdit, setLoadedForEdit] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existingFiles = (record?.files ?? []).filter((f) => !removedFileIds.includes(f.id));

  const onPickFiles = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    for (const f of picked) {
      if (f.size > 8 * 1024 * 1024) {
        toast({ title: "File too large", description: `${f.name} is over the 8 MB limit`, variant: "destructive" });
        return;
      }
    }
    setPendingFiles((prev) => [...prev, ...picked]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (!open) return;
    if (record) {
      setName(record.name);
      setPersonName(record.personName ?? "");
      setCategory(record.category);
      setNotes(record.notes ?? "");
      setLoadedForEdit(false);
      // Load current values for editing (audit-logged reveal)
      (async () => {
        try {
          const res = await apiRequest("POST", `/api/vault/records/${record.id}/reveal`);
          if (res.ok) {
            const fields = (await res.json()).fields as Record<string, string>;
            setFieldPairs(Object.entries(fields).map(([key, value]) => ({ key, value })));
          } else {
            setFieldPairs([]);
          }
        } catch {
          setFieldPairs([]);
        } finally {
          setLoadedForEdit(true);
        }
      })();
    } else {
      setName("");
      setPersonName("");
      setCategory("ssn");
      setNotes("");
      setFieldPairs(categoryMeta("ssn").fields.map((f) => ({ key: f, value: "" })));
      setLoadedForEdit(true);
    }
    setPendingFiles([]);
    setRemovedFileIds([]);
  }, [open, record?.id]);

  const onCategoryChange = (value: string) => {
    setCategory(value);
    if (!record) {
      const template = categoryMeta(value).fields;
      setFieldPairs(template.length ? template.map((f) => ({ key: f, value: "" })) : [{ key: "", value: "" }]);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const fields: Record<string, string> = {};
      for (const p of fieldPairs) {
        if (p.key.trim() && p.value.trim()) fields[p.key.trim()] = p.value.trim();
      }
      if (Object.keys(fields).length === 0 && pendingFiles.length === 0 && existingFiles.length === 0) {
        throw new Error("Add at least one field with a value, or attach a file");
      }
      const body = { name, personName: personName || null, category, fields, notes: notes || null };
      const res = record
        ? await apiRequest("PATCH", `/api/vault/records/${record.id}`, body)
        : await apiRequest("POST", "/api/vault/records", body);
      if (!res.ok) throw new Error((await res.json()).error || "Save failed");
      const recordId = record ? record.id : (await res.json()).id as string;

      // Delete removed attachments, then upload new ones (encrypted server-side)
      await Promise.all(removedFileIds.map((fid) => apiRequest("DELETE", `/api/vault/files/${fid}`)));
      const results = await Promise.all(
        pendingFiles.map(async (f) => {
          const dataBase64 = await readFileAsBase64(f);
          const up = await apiRequest("POST", `/api/vault/records/${recordId}/files`, {
            filename: f.name,
            mimeType: f.type || "application/octet-stream",
            dataBase64,
          });
          return { name: f.name, ok: up.ok };
        }),
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length) throw new Error(`Record saved, but upload failed for: ${failed.map((f) => f.name).join(", ")}`);
    },
    onSuccess: () => {
      toast({ title: record ? "Record updated" : "Record added" });
      queryClient.invalidateQueries({ queryKey: ["/api/vault/records"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vault/audit"] });
      onClose();
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="dialog-vault-record">
        <DialogHeader>
          <DialogTitle>{record ? "Edit Record" : "Add Vault Record"}</DialogTitle>
          <DialogDescription>
            Values are encrypted before they are stored, and are automatically shielded from AI services.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-w-2xl">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="record-name">Record name</Label>
              <Input id="record-name" placeholder={'e.g. "Sarah\'s SSN"'} value={name} onChange={(e) => setName(e.target.value)} data-testid="input-record-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-person">Family member (optional)</Label>
              <Input id="record-person" placeholder='e.g. "Sarah"' value={personName} onChange={(e) => setPersonName(e.target.value)} data-testid="input-record-person" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={category} onValueChange={onCategoryChange}>
              <SelectTrigger data-testid="select-record-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`category-option-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Secure fields</Label>
            {!loadedForEdit ? (
              <p className="text-sm text-muted-foreground">Loading current values…</p>
            ) : (
              <div className="space-y-2">
                {fieldPairs.map((pair, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder="Field name"
                      value={pair.key}
                      onChange={(e) => setFieldPairs((prev) => prev.map((p, j) => (j === i ? { ...p, key: e.target.value } : p)))}
                      className="w-1/3"
                      data-testid={`input-field-key-${i}`}
                    />
                    <Input
                      placeholder="Value (encrypted)"
                      value={pair.value}
                      onChange={(e) => setFieldPairs((prev) => prev.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)))}
                      className="flex-1"
                      data-testid={`input-field-value-${i}`}
                    />
                    <Button variant="ghost" size="icon" onClick={() => setFieldPairs((prev) => prev.filter((_, j) => j !== i))} title="Remove field">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setFieldPairs((prev) => [...prev, { key: "", value: "" }])} data-testid="button-add-field">
                    <Plus className="w-4 h-4 mr-1" /> Add Field
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="button-attach-file">
                    <Paperclip className="w-4 h-4 mr-1" /> Attach Image / File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => onPickFiles(e.target.files)}
                    data-testid="input-attach-file"
                  />
                </div>
              </div>
            )}
          </div>

          {(existingFiles.length > 0 || pendingFiles.length > 0) && (
            <div className="space-y-2">
              <Label>Attachments (encrypted)</Label>
              <div className="space-y-1">
                {existingFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-sm" data-testid={`dialog-file-${f.id}`}>
                    {f.mimeType.startsWith("image/") ? <ImageIcon className="w-4 h-4 shrink-0 text-muted-foreground" /> : <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />}
                    <span className="truncate">{f.filename}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.sizeBytes)}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setRemovedFileIds((prev) => [...prev, f.id])} title="Remove attachment">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {pendingFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`dialog-pending-file-${i}`}>
                    {f.type.startsWith("image/") ? <ImageIcon className="w-4 h-4 shrink-0 text-muted-foreground" /> : <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />}
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
                    <Badge variant="secondary" className="shrink-0">new</Badge>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} title="Remove">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Files are encrypted with your vault key before storage — up to 8 MB each.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="record-notes">Notes (not encrypted — no sensitive info here)</Label>
            <Textarea id="record-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="input-record-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()} data-testid="button-save-record">
            {saveMutation.isPending ? "Saving…" : record ? "Save Changes" : "Add Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Audit log ----------------

function AuditLog() {
  const { data, isLoading } = useQuery<{ entries: AuditEntry[] }>({ queryKey: ["/api/vault/audit"] });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5" /> Activity Log</CardTitle>
        <CardDescription>Every vault action is recorded — including when a value was shielded from an AI service.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div>Loading…</div>
        ) : !data?.entries.length ? (
          <p className="text-muted-foreground" data-testid="text-audit-empty">No activity yet.</p>
        ) : (
          <div className="space-y-2">
            {data.entries.map((e) => (
              <div key={e.id} className="flex items-start justify-between gap-4 text-sm border-b last:border-b-0 pb-2" data-testid={`audit-entry-${e.id}`}>
                <div className="min-w-0">
                  <span className="font-medium">{ACTION_LABELS[e.action] ?? e.action}</span>
                  {e.recordName && <span className="text-muted-foreground"> — {e.recordName}</span>}
                  {e.context && <div className="text-xs text-muted-foreground truncate">{e.context}</div>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {new Date(e.createdAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Security tab ----------------

function SecurityTab() {
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const changeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/vault/change-password", { currentPassword, newPassword });
      if (!res.ok) throw new Error((await res.json()).error || "Change failed");
    },
    onSuccess: () => {
      toast({ title: "Vault password changed" });
      setCurrentPassword(""); setNewPassword(""); setConfirm("");
    },
    onError: (e: any) => toast({ title: "Could not change password", description: e.message, variant: "destructive" }),
  });

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Change vault password</CardTitle>
        <CardDescription>Your records stay intact — they are re-secured under the new password.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="cur-pass">Current vault password</Label>
          <Input id="cur-pass" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="input-current-vault-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-pass">New vault password (min 8 characters)</Label>
          <Input id="new-pass" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="input-new-vault-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-pass">Confirm new password</Label>
          <Input id="confirm-pass" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="input-confirm-vault-password" />
          {confirm.length > 0 && confirm !== newPassword && <p className="text-sm text-destructive">Passwords do not match</p>}
        </div>
        <Button
          disabled={!currentPassword || newPassword.length < 8 || newPassword !== confirm || changeMutation.isPending}
          onClick={() => changeMutation.mutate()}
          data-testid="button-change-vault-password"
        >
          {changeMutation.isPending ? "Changing…" : "Change Password"}
        </Button>
      </CardContent>
    </Card>
  );
}
